// src/components/inspector/inspections/ReportTab.jsx
import React, { useState, useEffect } from 'react';
import inspectorAPI from '../../../services/inspectorAPI';
import styles from './ReportTab.module.css';

const SCORE_FIELDS = [
  { key: 'core_systems_score',        label: 'Core Systems',       icon: '⚙️',  desc: 'Engine, transmission, drivetrain' },
  { key: 'supporting_systems_score',  label: 'Supporting Systems', icon: '🔌',  desc: 'Electrical, fuel, cooling, brakes' },
  { key: 'interiors_ac_score',        label: 'Interiors & AC',     icon: '🪑',  desc: 'Cabin, seats, dashboard, climate' },
  { key: 'exteriors_lights_score',    label: 'Exteriors & Lights', icon: '💡',  desc: 'Body panels, paint, headlights' },
  { key: 'wear_tear_score',           label: 'Wear & Tear',        icon: '🔧',  desc: 'Tyres, wipers, belts, filters' },
];

const DEFAULT_FORM = {
  core_systems_score:       '',
  supporting_systems_score: '',
  interiors_ac_score:       '',
  exteriors_lights_score:   '',
  wear_tear_score:          '',
  overall_score:            '',
  meter_tampered:           false,
  flooded:                  false,
  next_service_due:         '',
};

function ScoreInput({ value, onChange, label }) {
  const num    = parseFloat(value);
  const filled = !isNaN(num);
  const color  = filled
    ? num >= 7 ? '#22c55e' : num >= 4 ? '#f59e0b' : '#ef4444'
    : '#94a3b8';

  return (
    <div className={styles.scoreField}>
      <div className={styles.scoreHeader}>
        <span className={styles.scoreLabel}>{label}</span>
        <span className={styles.scoreValue} style={{ color }}>
          {filled ? num.toFixed(1) : '—'}{' '}
          <span className={styles.scoreMax}>/10</span>
        </span>
      </div>
      <div className={styles.sliderWrap}>
        <input
          type="range"
          min="0" max="10" step="0.5"
          value={value || 0}
          onChange={(e) => onChange(e.target.value)}
          className={styles.slider}
          style={{ '--fill': color }}
        />
        <div className={styles.sliderTicks}>
          {[0, 2, 4, 6, 8, 10].map((t) => <span key={t}>{t}</span>)}
        </div>
      </div>
    </div>
  );
}

export default function ReportTab({ inspectionId, existingReport, onReportSaved }) {
  const [form,    setForm]    = useState(DEFAULT_FORM);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const hasReport = !!existingReport?.id;

  // Populate form when an existing report is passed in
  useEffect(() => {
    if (existingReport) {
      setForm({
        core_systems_score:       existingReport.core_systems_score       ?? '',
        supporting_systems_score: existingReport.supporting_systems_score ?? '',
        interiors_ac_score:       existingReport.interiors_ac_score       ?? '',
        exteriors_lights_score:   existingReport.exteriors_lights_score   ?? '',
        wear_tear_score:          existingReport.wear_tear_score          ?? '',
        overall_score:            existingReport.overall_score            ?? '',
        meter_tampered:           existingReport.meter_tampered           ?? false,
        flooded:                  existingReport.flooded                  ?? false,
        next_service_due: existingReport.next_service_due
          ? existingReport.next_service_due.slice(0, 10)
          : '',
      });
    }
  }, [existingReport]);

  // Auto-calculate overall score as simple average of the 5 section scores
  useEffect(() => {
    const scores = SCORE_FIELDS
      .map((f) => parseFloat(form[f.key]))
      .filter((v) => !isNaN(v));

    if (scores.length === SCORE_FIELDS.length) {
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      setForm((prev) => ({ ...prev, overall_score: avg.toFixed(1) }));
    }
  }, [
    form.core_systems_score,
    form.supporting_systems_score,
    form.interiors_ac_score,
    form.exteriors_lights_score,
    form.wear_tear_score,
  ]);

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const validate = () => {
    for (const f of SCORE_FIELDS) {
      if (form[f.key] === '' || isNaN(parseFloat(form[f.key]))) {
        return `Please set a score for "${f.label}"`;
      }
    }
    if (!form.next_service_due) return 'Please enter the next service due date';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        core_systems_score:       parseFloat(form.core_systems_score),
        supporting_systems_score: parseFloat(form.supporting_systems_score),
        interiors_ac_score:       parseFloat(form.interiors_ac_score),
        exteriors_lights_score:   parseFloat(form.exteriors_lights_score),
        wear_tear_score:          parseFloat(form.wear_tear_score),
        overall_score:            parseFloat(form.overall_score),
      };

      let res;
      if (hasReport) {
        res = await inspectorAPI.updateInspectionReport(inspectionId, payload);
      } else {
        res = await inspectorAPI.createInspectionReport(inspectionId, payload);
      }

      setSuccess(hasReport ? 'Report updated successfully!' : 'Report created successfully!');
      if (onReportSaved) onReportSaved(res.data);
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save report. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const overallNum   = parseFloat(form.overall_score);
  const overallColor = !isNaN(overallNum)
    ? overallNum >= 7 ? '#22c55e' : overallNum >= 4 ? '#f59e0b' : '#ef4444'
    : '#94a3b8';

  const scored     = SCORE_FIELDS.map((f) => parseFloat(form[f.key])).filter((v) => !isNaN(v));
  const goodCount  = scored.filter((v) => v >= 7).length;
  const fairCount  = scored.filter((v) => v >= 4 && v < 7).length;
  const poorCount  = scored.filter((v) => v < 4).length;

  return (
    <div className={styles.container}>

      {/* ── Summary bar ── */}
      <div className={styles.summaryBar}>
        <div className={styles.summaryCard}>
          <span className={styles.summaryIcon}>📋</span>
          <span className={styles.summaryNum}>{scored.length}</span>
          <span className={styles.summaryLbl}>Items Scored</span>
        </div>
        <div className={`${styles.summaryCard} ${styles.good}`}>
          <span className={styles.summaryIcon}>✅</span>
          <span className={styles.summaryNum}>{goodCount}</span>
          <span className={styles.summaryLbl}>Good</span>
        </div>
        <div className={`${styles.summaryCard} ${styles.fair}`}>
          <span className={styles.summaryIcon}>⚠️</span>
          <span className={styles.summaryNum}>{fairCount}</span>
          <span className={styles.summaryLbl}>Fair</span>
        </div>
        <div className={`${styles.summaryCard} ${styles.poor}`}>
          <span className={styles.summaryIcon}>❌</span>
          <span className={styles.summaryNum}>{poorCount}</span>
          <span className={styles.summaryLbl}>Poor</span>
        </div>
      </div>

      <div className={styles.grid}>

        {/* ── Left: section scores ── */}
        <div className={styles.card}>
          <div className={styles.cardTitle}>Section Scores</div>
          <p className={styles.cardHint}>Rate each section from 0 (worst) to 10 (best)</p>

          <div className={styles.scoreList}>
            {SCORE_FIELDS.map((f) => (
              <div key={f.key} className={styles.scoreRow}>
                <div className={styles.scoreRowMeta}>
                  <span className={styles.scoreRowIcon}>{f.icon}</span>
                  <div>
                    <div className={styles.scoreRowLabel}>{f.label}</div>
                    <div className={styles.scoreRowDesc}>{f.desc}</div>
                  </div>
                </div>
                <ScoreInput
                  label={f.label}
                  value={form[f.key]}
                  onChange={(v) => set(f.key, v)}
                />
              </div>
            ))}
          </div>

          {/* Overall score */}
          <div className={styles.overallBadge} style={{ borderColor: overallColor }}>
            <span className={styles.overallLabel}>Overall Score</span>
            <span className={styles.overallValue} style={{ color: overallColor }}>
              {isNaN(overallNum) ? '—' : overallNum.toFixed(1)}
              <span className={styles.overallMax}>/10</span>
            </span>
            <span className={styles.overallHint}>Auto-calculated from section scores</span>
          </div>
        </div>

        {/* ── Right: flags + service + submit ── */}
        <div className={styles.rightCol}>
          <div className={styles.card}>
            <div className={styles.cardTitle}>Inspection Flags</div>
            <p className={styles.cardHint}>Check any issues found during inspection</p>

            <div className={styles.flags}>
              <label className={`${styles.flagRow} ${form.meter_tampered ? styles.flagActive : ''}`}>
                <div className={styles.flagInfo}>
                  <span className={styles.flagIcon}>🔢</span>
                  <div>
                    <div className={styles.flagLabel}>Odometer Tampered</div>
                    <div className={styles.flagDesc}>Meter shows signs of manipulation</div>
                  </div>
                </div>
                <div className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={form.meter_tampered}
                    onChange={(e) => set('meter_tampered', e.target.checked)}
                    className={styles.toggleInput}
                  />
                  <span className={`${styles.toggleSlider} ${form.meter_tampered ? styles.toggleOn : ''}`} />
                </div>
              </label>

              <label className={`${styles.flagRow} ${form.flooded ? styles.flagActive : ''}`}>
                <div className={styles.flagInfo}>
                  <span className={styles.flagIcon}>🌊</span>
                  <div>
                    <div className={styles.flagLabel}>Flood Damaged</div>
                    <div className={styles.flagDesc}>Vehicle shows signs of water damage</div>
                  </div>
                </div>
                <div className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={form.flooded}
                    onChange={(e) => set('flooded', e.target.checked)}
                    className={styles.toggleInput}
                  />
                  <span className={`${styles.toggleSlider} ${form.flooded ? styles.toggleOn : ''}`} />
                </div>
              </label>
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardTitle}>Service Information</div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Next Service Due Date</label>
              <input
                type="date"
                value={form.next_service_due}
                onChange={(e) => set('next_service_due', e.target.value)}
                className={styles.dateInput}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          {error   && <div className={`${styles.alert} ${styles.alertError}`}><span>⚠️</span> {error}</div>}
          {success && <div className={`${styles.alert} ${styles.alertSuccess}`}><span>✅</span> {success}</div>}

          <button
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving
              ? '⏳ Saving...'
              : hasReport
              ? '💾 Update Report'
              : '📝 Create Report'}
          </button>

          {hasReport && (
            <div className={styles.reportStatus}>
              <span className={styles.reportStatusDot} />
              Report exists — you are editing it
            </div>
          )}
        </div>
      </div>
    </div>
  );
}